import React from 'react';
import { CheckIcon } from 'lucide-react';
import type { MockBadge, MockCard, Tone } from '../../../types/product';
const toneText: Record<Tone, string> = {
  default: 'text-ink',
  positive: 'text-[#1B7A3D]',
  negative: 'text-[#C0362C]',
  warning: 'text-[#9A5B15]',
  brand: 'text-brand'
};
const toneBadge: Record<Tone, string> = {
  default: 'bg-canvas-warm text-ink',
  positive: 'bg-[#E9F6EC] text-[#1B7A3D]',
  negative: 'bg-[#FCEBEA] text-[#C0362C]',
  warning: 'bg-[#FBF1E3] text-[#9A5B15]',
  brand: 'bg-brand-soft text-brand'
};
const toneBar: Record<Tone, string> = {
  default: 'bg-ink/70',
  positive: 'bg-[#2E9A55]',
  negative: 'bg-[#D9483C]',
  warning: 'bg-[#D98A2B]',
  brand: 'bg-brand'
};
type Size = 'md' | 'sm';
type MockCardViewProps = {
  card: MockCard;
  size?: Size;
  flat?: boolean;
};
export function MockCardView({
  card,
  size = 'md',
  flat = false
}: MockCardViewProps) {
  const sm = size === 'sm';
  const shell = `w-full rounded-[14px] bg-white ${sm ? 'p-3.5' : 'p-5'} ${flat ? 'ring-1 ring-canvas-line' : 'shadow-[0_18px_40px_-24px_rgba(11,11,11,0.28)]'}`;
  const titleCls = `${sm ? 'text-[11px]' : 'text-[14px]'} font-semibold text-ink`;
  const metaCls = `${sm ? 'text-[9px]' : 'text-[12px]'} text-ink-muted`;
  const rowText = sm ? 'text-[9.5px]' : 'text-[13px]';
  const subText = sm ? 'text-[8px]' : 'text-[11.5px]';
  const header = (title: string, meta?: string, badge?: MockBadge) => <div className="flex items-center justify-between gap-2" data-id="element-1107" data-mp="2bgn0o6wr6e3k">
      <div className="min-w-0" data-id="element-1108" data-mp="2gdn8ond5xqxb">
        <p className={titleCls} data-id="element-1109" data-mp="2geow7rbd85vx">{title}</p>
        {meta && <p className={`mt-0.5 ${metaCls}`} data-id="element-1110" data-mp="2kg0d98g6bisi">{meta}</p>}
      </div>
      {badge && <Badge badge={badge} sm={sm} data-id="element-1111" data-mp="2khuwmibsr2uh" />}
    </div>;
  const action = (label?: string) => label ? <span className={`mt-4 block rounded-[8px] bg-brand text-center font-semibold text-white ${sm ? 'py-1.5 text-[9px]' : 'py-2.5 text-[12.5px]'}`} data-id="element-1112" data-mp="2pch264v7616n">
        {label}
      </span> : null;
  if (card.type === 'list') {
    return <div className={shell} data-id="element-1113" data-mp="20q67h2u19gxd">
        {header(card.title, card.meta, card.badge)}
        <ul className={`${sm ? 'mt-2' : 'mt-3'} divide-y divide-canvas-line`} data-id="element-1114" data-mp="1wpdtjwa7unur">
          {card.rows.map(row => <li key={row.label + row.value} className={`flex items-center justify-between gap-3 ${sm ? 'py-1.5' : 'py-2.5'}`} data-id="element-1115" data-mp="2ap5g4lestckd">
              <span className="min-w-0" data-id="element-1116" data-mp="1i199anvim49v">
                <span className={`block truncate ${rowText} text-ink-soft`} data-id="element-1117" data-mp="1i2axzv5gepa2">{row.label}</span>
                {row.sublabel && <span className={`block truncate ${subText} text-ink-muted`} data-id="element-1118" data-mp="1d3hglrn1qe4m">{row.sublabel}</span>}
              </span>
              <span className={`shrink-0 ${rowText} font-semibold ${toneText[row.tone ?? 'default']}`} data-id="element-1119" data-mp="1rsdpts0u9x6t">{row.value}</span>
            </li>)}
        </ul>
        {card.footer && <div className={`flex items-center justify-between border-t border-canvas-line ${sm ? 'pt-2' : 'pt-3'}`} data-id="element-1120" data-mp="z972034rk0ca">
            <span className={`${rowText} font-semibold text-ink`} data-id="element-1121" data-mp="u9kortoxqk3h">{card.footer.label}</span>
            <span className={`${sm ? 'text-[11px]' : 'text-[16px]'} font-semibold tracking-[-0.02em] text-ink`} data-id="element-1122" data-mp="35xpivb2bs3dj">
              {card.footer.value}
            </span>
          </div>}
        {action(card.action)}
      </div>;
  }
  if (card.type === 'bars') {
    const max = Math.max(...card.bars.map(b => b.value));
    return <div className={shell} data-id="element-1123" data-mp="3op06njcr8017">
        <p className={titleCls} data-id="element-1124" data-mp="3oqtf946xgubu">{card.title}</p>
        <div className="mt-1 flex items-baseline gap-2" data-id="element-1125" data-mp="3tomippguja8f">
          <p className={`${sm ? 'text-[15px]' : 'text-[24px]'} font-semibold tracking-[-0.02em] text-ink`} data-id="element-1126" data-mp="3to3006jdghtc">{card.value}</p>
          {card.delta && <span className={`${sm ? 'text-[9px]' : 'text-[12px]'} font-medium ${toneText[card.delta.tone]}`} data-id="element-1127" data-mp="3knoep76er2pu">
              {card.delta.label}
            </span>}
        </div>
        <div className={`flex items-end gap-2 ${sm ? 'mt-2 h-[64px]' : 'mt-5 h-[110px]'}`} data-id="element-1128" data-mp="298rlrm73rtd7">
          {card.bars.map(bar => <div key={bar.label} className="flex h-full flex-1 flex-col items-center justify-end gap-1" data-id="element-1129" data-mp="2oio8syq02n5y">
              <div className={`w-full max-w-[22px] rounded-t-[3px] ${bar.highlight ? 'bg-brand' : 'bg-brand/25'}`} style={{
            height: `${Math.max(8, bar.value / max * 100)}%`
          }} data-id="element-1130" data-mp="2jqn2rujfdsod" />
              <span className={`${sm ? 'text-[7.5px]' : 'text-[11px]'} text-ink-muted`} data-id="element-1131" data-mp="2askr8l22fhmu">{bar.label}</span>
            </div>)}
        </div>
        {card.legend && <p className={`border-t border-canvas-line ${sm ? 'mt-2 pt-1.5 text-[8px]' : 'mt-3 pt-3 text-[11.5px]'} text-ink-muted`} data-id="element-1132" data-mp="32j8vw72y7559">
            {card.legend}
          </p>}
      </div>;
  }
  if (card.type === 'progress') {
    return <div className={shell} data-id="element-1133" data-mp="2tju2ayrhiegj">
        {header(card.title, card.meta, card.badge)}
        <ul className={`${sm ? 'mt-2.5 space-y-2' : 'mt-4 space-y-3.5'}`} data-id="element-1134" data-mp="1mzcf544dpzzd">
          {card.rows.map(row => <li key={row.label} data-id="element-1135" data-mp="1i4oyfrfma9vc">
              <div className={`flex items-center justify-between ${rowText}`} data-id="element-1136" data-mp="3tqh36akx6gib">
                <span className="text-ink-soft" data-id="element-1137" data-mp="2j4xarphlphp">{row.label}</span>
                <span className={`font-semibold ${toneText[row.tone ?? 'default']}`} data-id="element-1138" data-mp="2jmsrm4brzok">{row.value}</span>
              </div>
              <div className={`${sm ? 'mt-1 h-1' : 'mt-1.5 h-1.5'} w-full overflow-hidden rounded-full bg-canvas-line`} data-id="element-1139" data-mp="bbomz2nswjqe">
                <div className={`h-full rounded-full ${toneBar[row.tone && row.tone !== 'default' ? row.tone : 'brand']}`} style={{
              width: `${row.pct}%`
            }} data-id="element-1140" data-mp="gbb25hn6wa1u" />
              </div>
            </li>)}
        </ul>
        {action(card.action)}
      </div>;
  }
  const r = 30;
  const circ = 2 * Math.PI * r;
  return <div className={shell} data-id="element-1141" data-mp="2s0bzur37dfdf">
      {header(card.title, card.meta)}
      <div className={`flex items-center gap-4 ${sm ? 'mt-2.5' : 'mt-5'}`} data-id="element-1142" data-mp="2s1wxwqoq7gg1">
        <svg viewBox="0 0 72 72" className={sm ? 'h-12 w-12' : 'h-[68px] w-[68px]'} aria-hidden="true" data-id="element-1143" data-mp="2w2yut0v67geu">
          <circle cx="36" cy="36" r={r} fill="none" stroke="#E3E0DC" strokeWidth="9" data-id="element-1144" data-mp="388fgbdpwwcz" />
          <circle cx="36" cy="36" r={r} fill="none" stroke="#1C8CDE" strokeWidth="9" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - card.pct / 100)} transform="rotate(-90 36 36)" data-id="element-1145" data-mp="3ues0skh6k4pj" />
        </svg>
        <div data-id="element-1146" data-mp="m3yky8kv0avm">
          <p className={`${sm ? 'text-[15px]' : 'text-[22px]'} font-semibold tracking-[-0.02em] text-ink`} data-id="element-1147" data-mp="h7hw1c5u5shh">{card.pct}%</p>
          <p className={metaCls} data-id="element-1148" data-mp="vwe0z0clipdv">{card.pctLabel}</p>
        </div>
      </div>
      <ul className={`border-t border-canvas-line ${sm ? 'mt-2.5 space-y-1.5 pt-2' : 'mt-5 space-y-2.5 pt-4'}`} data-id="element-1149" data-mp="qx9tce5bkzpn">
        {card.checks.map(c => <li key={c} className={`flex items-center gap-2 ${rowText} text-ink-soft`} data-id="element-1150" data-mp="14xc7bpaib8zm">
            <span className={`flex shrink-0 items-center justify-center rounded-full bg-brand-soft ${sm ? 'h-3 w-3' : 'h-4 w-4'}`} data-id="element-1151" data-mp="zyr9txmn33ev">
              <CheckIcon className={`${sm ? 'h-2 w-2' : 'h-2.5 w-2.5'} text-brand`} strokeWidth={3} aria-hidden="true" data-id="element-1152" data-mp="1jhey3o64t0m7" />
            </span>
            {c}
          </li>)}
      </ul>
      {action(card.action)}
    </div>;
}
function Badge({
  badge,
  sm
}: {
  badge: MockBadge;
  sm: boolean;
}) {
  return <span className={`shrink-0 rounded-full font-semibold ${toneBadge[badge.tone]} ${sm ? 'px-1.5 py-0.5 text-[8px]' : 'px-2.5 py-1 text-[11px]'}`} data-id="element-1153" data-mp="1em9gh1jri83x">
      {badge.label}
    </span>;
}